"use client"

import * as React from "react"
import { IconSparkles, IconSend } from "@tabler/icons-react"
import { toast } from "sonner"

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { fetchWithAuth } from "@/data/api/client"

const AREA_OPTIONS = [
  { value: "cost-explorer", label: "Cost Explorer" },
  { value: "dashboards", label: "Dashboards" },
  { value: "reports", label: "Reports" },
  { value: "alerts", label: "Alerts" },
  { value: "integrations", label: "Integrations" },
  { value: "organization", label: "Organization & Team" },
  { value: "other", label: "Other" },
]

const PAIN_LEVEL_OPTIONS = [
  { value: "nice-to-have", label: "Nice to have" },
  { value: "would-help", label: "Would help my workflow" },
  { value: "really-need", label: "I really need this" },
  { value: "blocking", label: "It's blocking me" },
]

export function FeatureRequestModal() {
  const [open, setOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [problem, setProblem] = React.useState("")
  const [area, setArea] = React.useState("")
  const [painLevel, setPainLevel] = React.useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!problem.trim()) {
      toast.error("Please describe the problem you're trying to solve")
      return
    }

    if (!area) {
      toast.error("Please select which area this is about")
      return
    }

    setIsSubmitting(true)

    try {
      await fetchWithAuth("/api/v1/feature-requests", {
        method: "POST",
        body: JSON.stringify({
          problem: problem.trim(),
          area,
          pain_level: painLevel || null,
        }),
      })

      toast.success("Thanks for sharing! We'll review your request soon.", {
        description: "Your feedback helps shape our roadmap.",
      })

      // Reset form and close
      setProblem("")
      setArea("")
      setPainLevel("")
      setOpen(false)
    } catch (error) {
      console.error("Failed to submit feature request:", error)
      toast.error("Failed to submit your request. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      // Reset form when closing
      setProblem("")
      setArea("")
      setPainLevel("")
    }
  }

  return (
    <>
      <FeatureRequestButton onClick={() => setOpen(true)} />

      <Modal open={open} onOpenChange={handleOpenChange}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A] flex items-center gap-2">
              <IconSparkles className="size-5 text-[#34C4A1]" />
              Request a Feature
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Help us build what matters most to you.
            </ModalDescription>
          </ModalHeader>

          <form onSubmit={handleSubmit}>
            <div className="px-[24px] py-[24px] space-y-[20px]">
              {/* Problem Field */}
              <div className="space-y-[8px]">
                <Label htmlFor="problem" className="text-[13px] text-[#08090A]">
                  What problem are you trying to solve? *
                </Label>
                <textarea
                  id="problem"
                  value={problem}
                  onChange={(e) => setProblem(e.target.value)}
                  placeholder="e.g., I need to compare costs across multiple cloud providers at once..."
                  disabled={isSubmitting}
                  className={cn(
                    "flex min-h-[100px] w-full px-[13px] py-[10px] rounded-[6px] border border-[#EDEEF3] bg-[#FFFFFF]",
                    "text-[13px] text-[#08090A] placeholder:text-[#8A8F98]",
                    "hover:border-[#D1D5DB] focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[#654EDA]",
                    "transition-colors resize-none",
                    "disabled:cursor-not-allowed disabled:opacity-50"
                  )}
                />
              </div>

              {/* Area Field */}
              <div className="space-y-[8px]">
                <Label htmlFor="area" className="text-[13px] text-[#08090A]">
                  Which area is this about? *
                </Label>
                <Select value={area} onValueChange={setArea} disabled={isSubmitting}>
                  <SelectTrigger className="w-full h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] hover:border-[#D1D5DB] focus:ring-0 focus:border-[#654EDA] data-[placeholder]:text-[#8A8F98]">
                    <SelectValue placeholder="Select an area" />
                  </SelectTrigger>
                  <SelectContent>
                    {AREA_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Pain Level Field (Optional) */}
              <div className="space-y-[8px]">
                <Label htmlFor="pain-level" className="text-[13px] text-[#08090A]">
                  How painful is this?
                  <span className="text-[#8A8F98] font-normal ml-1">(optional)</span>
                </Label>
                <Select value={painLevel} onValueChange={setPainLevel} disabled={isSubmitting}>
                  <SelectTrigger className="w-full h-[36px] px-[13px] rounded-[6px] border-[#EDEEF3] bg-[#FFFFFF] text-[13px] text-[#08090A] hover:border-[#D1D5DB] focus:ring-0 focus:border-[#654EDA] data-[placeholder]:text-[#8A8F98]">
                    <SelectValue placeholder="Select urgency level" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAIN_LEVEL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <span className="flex items-center gap-2">
                          <span>{option.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Footer message */}
              <p className="text-[12px] text-[#8A8F98]">
                We read every request. Power users often shape our roadmap.
              </p>
            </div>

            {/* Footer */}
            <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
              <Button
                type="button"
                variant="transparent-outline"
                size="sm"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
                className="h-[32px] font-medium border-[#EDEEF3]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !problem.trim() || !area}
                size="sm"
                className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <IconSend className="size-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  )
}

/**
 * Special feature request button with unique styling
 * Designed to stand out and motivate users to share feedback
 * Uses teal color - fresh, positive, encouraging
 */
export const FeatureRequestButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      className={cn(
        // Base layout - slightly smaller
        "group relative flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium",
        // Cursor
        "cursor-pointer",
        // Unique gradient border effect - using teal from palette
        "bg-gradient-to-r from-[#34C4A1]/8 to-[#46C2A4]/8",
        "border border-[#34C4A1]/25",
        // Text styling
        "text-[#2BA386]",
        // Hover state - subtle glow effect
        "transition-all duration-200",
        "hover:border-[#34C4A1]/50 hover:bg-gradient-to-r hover:from-[#34C4A1]/15 hover:to-[#46C2A4]/15",
        "hover:shadow-[0_0_12px_rgba(52,196,161,0.2)]",
        // Active state
        "active:scale-[0.98]",
        className
      )}
      {...props}
    >
      <IconSparkles className="size-3.5 text-[#34C4A1] transition-transform duration-200 group-hover:scale-110" />
      <span className="flex-1 text-left">Request a Feature</span>
    </button>
  )
})
FeatureRequestButton.displayName = "FeatureRequestButton"
